import {
  faCheck,
  faEyeSlash,
  faFastForward,
  faThumbsDown,
  faTrashCanXmark,
} from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  FlexBox,
  InfoSolidIcon,
  JoonUIColor,
  Modal,
  ModalHeader,
  SPACING,
  TextButton,
  Typography,
  UserRole,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import ToggleQuestActiveModal, {
  ToggleQuestActiveModalType,
  useToggleQuestActiveModalStore,
} from "./ToggleQuestActiveModal"
import useQuestBoardMultiSelect from "./useQuestBoardMultiSelect"
import useQuestBoardMultiSelectStore from "./useQuestBoardMultiSelectStore"
import useMobile from "../../hooks/useMobile"
import { useQuestBoardQuery } from "../../networking/queries"
import { useDeleteQuestsMutation } from "../../networking/quests/mutations"

const QuestBoardMultiSelect = () => {
  const {
    selectedQuestIds,
    isMultiSelectMode,
    setMultiSelectMode,
    clearSelectedQuestIds,
  } = useQuestBoardMultiSelectStore()
  const { data: quests } = useQuestBoardQuery()
  const { onClickCompleteQuests, onClickRejectQuests, onClickSkipQuests } =
    useQuestBoardMultiSelect()
  const { onOpen: openDeleteQuestModal } = useDeleteQuestModalStore()
  const { onOpen: openToggleQuestActiveModal } =
    useToggleQuestActiveModalStore()
  const { t } = useTranslation()

  const isMobile = useMobile()

  const hasTherapistModifiedQuest = selectedQuestIds.some((questId) => {
    const quest = quests?.find((quest) => quest.id === questId)
    const isTherapistCreated =
      quest?.series.assigner_profile?.role === UserRole.THERAPIST
    const isTherapistModified = !!quest?.series.current_frequency_target
    return isTherapistCreated || isTherapistModified
  })

  const handleSetQuestInactive = () => {
    const questSeriesIds =
      quests
        ?.filter((quest) => selectedQuestIds.includes(quest.id))
        .map((quest) => quest.series.id) ?? []

    if (questSeriesIds)
      openToggleQuestActiveModal(
        questSeriesIds,
        ToggleQuestActiveModalType.INACTIVE
      )
  }

  const isDeleteDisabled = hasTherapistModifiedQuest

  if (!isMultiSelectMode) return <></>

  return (
    <>
      <FlexBox
        direction="column"
        align="center"
        justify="center"
        gap={SPACING.space2}
        style={{
          position: "fixed",
          background: JoonUIColor.background.primaryNeutral,
          boxShadow: `0px 0px 10px 0px rgba(0, 0, 0, 0.1)`,
          borderTopLeftRadius: SPACING.space4,
          borderTopRightRadius: SPACING.space4,
          bottom: 0,
          left: isMobile ? 0 : "unset",
          padding: `${isDeleteDisabled ? SPACING.space2 : SPACING.space1} ${SPACING.space3}`,
          width: isMobile ? "100vw" : "600px",
          zIndex: 900,
        }}
      >
        {isDeleteDisabled && (
          <FlexBox
            direction="row"
            align="center"
            wrap={false}
            gap={SPACING.space1}
            style={{
              padding: SPACING.space2,
              background: JoonUIColor.background.lightBlue,
              borderRadius: SPACING.space2,
            }}
          >
            <InfoSolidIcon size={18} color={JoonUIColor.semantic.info} />
            <Typography
              variant="bodySmallBold"
              color={JoonUIColor.semantic.info}
            >
              {t("QB_THERAPIST_DELETE_INFO")}
            </Typography>
          </FlexBox>
        )}

        <FlexBox
          direction="row"
          align="center"
          justify="space-between"
          gap={SPACING.space2}
          wrap={false}
          style={{
            borderBottom: `1px solid ${JoonUIColor.border.default}`,
            padding: SPACING.space05,
          }}
        >
          <Typography variant="bodySmall">
            {selectedQuestIds.length === 1
              ? t("QB_FOR_SELECTED_ONE", { count: selectedQuestIds.length })
              : t("QB_FOR_SELECTED_OTHER", { count: selectedQuestIds.length })}
          </Typography>
          <TextButton
            onClick={() => {
              setMultiSelectMode(false)
              clearSelectedQuestIds()
            }}
          >
            <Typography
              variant="bodyXSmall"
              color={JoonUIColor.text.primaryAccent}
            >
              {t("QB_UNSELECT_ALL")}
            </Typography>
          </TextButton>
        </FlexBox>
        <FlexBox
          direction="row"
          align="center"
          justify="space-around"
          gap={SPACING.space2}
          wrap={false}
          style={{ padding: `${SPACING.space2} 0` }}
        >
          <button
            style={{ cursor: isDeleteDisabled ? "not-allowed" : "pointer" }}
            onClick={handleSetQuestInactive}
          >
            <FlexBox
              align="center"
              justify="center"
              direction="column"
              gap={SPACING.space1}
              wrap={false}
            >
              <FontAwesomeIcon
                icon={faEyeSlash}
                size="xl"
                color={
                  isDeleteDisabled
                    ? JoonUIColor.icon.light
                    : JoonUIColor.icon.neutral
                }
              />
              <Typography
                variant="bodySmallBold"
                color={
                  isDeleteDisabled
                    ? JoonUIColor.icon.light
                    : JoonUIColor.icon.neutral
                }
              >
                {t("QB_SET_INACTIVE")}
              </Typography>
            </FlexBox>
          </button>
          <button
            style={{ cursor: isDeleteDisabled ? "not-allowed" : "pointer" }}
            onClick={() => {
              !isDeleteDisabled && openDeleteQuestModal()
            }}
          >
            <FlexBox
              align="center"
              justify="center"
              direction="column"
              gap={SPACING.space1}
              wrap={false}
            >
              <FontAwesomeIcon
                icon={faTrashCanXmark}
                size="xl"
                color={
                  isDeleteDisabled
                    ? JoonUIColor.icon.light
                    : JoonUIColor.icon.neutral
                }
              />
              <Typography
                variant="bodySmallBold"
                color={
                  isDeleteDisabled
                    ? JoonUIColor.icon.light
                    : JoonUIColor.icon.neutral
                }
              >
                {t("QB_DELETE")}
              </Typography>
            </FlexBox>
          </button>
          <button style={{ padding: "0" }} onClick={onClickRejectQuests}>
            <FlexBox
              align="center"
              justify="center"
              direction="column"
              gap={SPACING.space1}
              wrap={false}
            >
              <FontAwesomeIcon
                icon={faThumbsDown}
                size="xl"
                color={JoonUIColor.icon.neutral}
              />
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.icon.neutral}
              >
                {t("QB_REJECT")}
              </Typography>
            </FlexBox>
          </button>
          <button style={{ padding: "0" }} onClick={onClickSkipQuests}>
            <FlexBox
              align="center"
              justify="center"
              direction="column"
              gap={SPACING.space1}
              wrap={false}
            >
              <FontAwesomeIcon
                icon={faFastForward}
                size="xl"
                color={JoonUIColor.icon.neutral}
              />
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.icon.neutral}
              >
                {t("QB_SKIP")}
              </Typography>
            </FlexBox>
          </button>
          <button style={{ padding: "0" }} onClick={onClickCompleteQuests}>
            <FlexBox
              align="center"
              justify="center"
              direction="column"
              gap={SPACING.space1}
              wrap={false}
            >
              <FontAwesomeIcon
                icon={faCheck}
                size="xl"
                color={JoonUIColor.semantic.success}
              />
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.icon.neutral}
              >
                {t("QB_COMPLETE")}
              </Typography>
            </FlexBox>
          </button>
        </FlexBox>
      </FlexBox>
      <DeleteQuestModal />
      <ToggleQuestActiveModal />
    </>
  )
}

interface DeleteQuestModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

const useDeleteQuestModalStore = create<DeleteQuestModalStore>((set) => ({
  isOpen: false,
  onOpen: () => set({ isOpen: true }),
  onClose: () => set({ isOpen: false }),
}))

const DeleteQuestModal = () => {
  const { isOpen, onClose } = useDeleteQuestModalStore()
  const { data } = useQuestBoardQuery()
  const { selectedQuestIds, setMultiSelectMode } =
    useQuestBoardMultiSelectStore()
  const { t } = useTranslation()

  const deleteQuestsMutation = useDeleteQuestsMutation()

  const onClickDeleteQuests = async () => {
    if (!data) return

    // get list of quest series ids from the selected (instance) ids
    const questSeriesIds = selectedQuestIds
      .map((questId) => data.find((quest) => quest.id === questId)?.series.id)
      .filter((id) => !!id) as number[]

    await deleteQuestsMutation.mutateAsync(questSeriesIds)
    setMultiSelectMode(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} animate>
      <ModalHeader
        title={t("QB_DELETE_QUEST_TITLE")}
        onClose={onClose}
        showBorderBottom
      />
      <FlexBox
        direction="column"
        align="center"
        justify="center"
        gap={SPACING.space2}
        style={{
          padding: SPACING.space6,
          paddingTop: SPACING.space4,
        }}
      >
        <Typography
          variant="body"
          color={JoonUIColor.text.primary}
          textAlign="left"
        >
          {selectedQuestIds.length === 1
            ? t("QB_DELETE_CONFIRM_ONE")
            : t("QB_DELETE_CONFIRM_OTHER")}
        </Typography>
        <Typography variant="body" textAlign="left">
          {t("QB_DELETE_UNDONE")}
        </Typography>
        <FlexBox
          direction="row"
          align="center"
          justify="flex-end"
          gap={SPACING.space4}
          wrap={false}
          style={{ padding: `${SPACING.space2} 0`, marginTop: SPACING.space2 }}
        >
          <TextButton onClick={onClose}>
            <Typography
              variant="bodyBold"
              color={JoonUIColor.text.primaryAccent}
            >
              {t("QB_CANCEL")}
            </Typography>
          </TextButton>
          <Button
            text={t("QB_DELETE_QUESTS_BTN")}
            onClick={onClickDeleteQuests}
            buttonType="redPrimary"
            isLoading={deleteQuestsMutation.isLoading}
          />
        </FlexBox>
      </FlexBox>
    </Modal>
  )
}

export default QuestBoardMultiSelect
